import { capitalize, titleize } from '../utils/formatter';
import { AssetType, IPersonAsset, VehicleUsageType } from './IPersonAsset';

export enum VehicleKind {
  VAN = 'van',
  BUS = 'bus',
  WAGON = 'wagon',
  SEDAN = 'sedan',
  TRUCK = 'truck',
  PICKUP = 'pickup',
  LUXURY = 'luxury',
  TRAILER = 'trailer',
  UNKNOWN = 'unknown',
  HATCHBACK = 'hatchback',
  MOTORCYCLE = 'motorcycle',
  CONVERTIBLE = 'convertible',
  COUPE_2_DOOR = 'coupe 2 door',
  PASSENGER_CAR = 'passenger car',
  INCOMPLETE_VEHICLE = 'incomplete vehicle',
  MPV = 'multipurpose passenger vehicle (mpv)',
  ALL_TERRAIN_VEHICLE = 'all terrain vehicle',
  BOAT = 'boat'
}

export enum VehicleFields {
  VIN = 'vin',
  Usage = 'usage',
  DaysPerWeekDriven = 'days_per_week_driven',
  AnnualMileage = 'annual_mileage',
  PlateType = 'plate_type',
  PlateNumber = 'plate_number',
  Ownership = 'ownership',
  UsedForRidesharing = 'used_for_ridesharing',
  Address = 'address',
  OneWayMileage = 'one_way_mileage',
  Kind = 'kind',
  Year = 'year',
  Make = 'make',
  Model = 'model',
  Submodel = 'submodel',
  Type = 'type'
}

export const isAutoAssetNotQuotable = (vehicleKind: VehicleKind | null): boolean => {
  if (!vehicleKind) {
    return false;
  }
  return [
    VehicleKind.TRAILER,
    VehicleKind.MOTORCYCLE,
    VehicleKind.INCOMPLETE_VEHICLE,
    VehicleKind.BUS,
    VehicleKind.ALL_TERRAIN_VEHICLE,
    VehicleKind.BOAT
  ].includes(vehicleKind);
};

export const notMotorcycleOrTrailerOrBoat = (kind: IVehicle['kind']) =>
  kind !== VehicleKind.MOTORCYCLE && kind !== VehicleKind.TRAILER && kind !== VehicleKind.BOAT;

export interface IVehicle extends IPersonAsset {
  asset_name: AssetType.Auto;
  usage: VehicleUsageType | null;
  make: string | null;
  model: string | null;
  kind: VehicleKind | null;
  submodel: string | null;
  year: number | null;
  vin: string | null;
  plate_number: string | null;
  plate_type: string | null;
  annual_mileage: number | null;
  distance_driven_daily: number | null;
  days_per_week_driven: number | null;
  used_for_ridesharing: boolean | null;
  last_service_date: string | null;
  last_service_writer_name: string | null;
  first_owner: string | null;
  sale_person_name: string | null;
  one_way_mileage: number | null;
}

export const buildVehicleTitle = (vehicle?: {
  year: number | string | null;
  make: string | null;
  model: string | null;
  vin: string | null;
  description_without_icon?: string;
}) => {
  if (!vehicle) {
    return 'Unknown vehicle';
  }
  if (vehicle.year && vehicle.make && vehicle.model) {
    return titleize(`${vehicle.year} ${vehicle.make} ${vehicle.model}`);
  }
  return capitalize(vehicle.vin) || vehicle.description_without_icon || 'Unknown vehicle';
};

export const generateAutoDescription = (assets: IVehicle[] | null) =>
  assets?.map(auto => buildVehicleTitle(auto)).join(', ') ?? '';

export const VIN_REGEXP = /^[A-HJ-NPR-Z0-9]{17}$/i;

export type VehicleType = 'car' | 'classic_car' | 'truck' | 'moto' | 'rv' | 'boat';
const vehicleTypeToKindMap: Record<VehicleType, VehicleKind> = {
  car: VehicleKind.PASSENGER_CAR,
  classic_car: VehicleKind.LUXURY,
  truck: VehicleKind.TRUCK,
  moto: VehicleKind.MOTORCYCLE,
  rv: VehicleKind.TRAILER,
  boat: VehicleKind.BOAT
};

export const vehicleKindToTypeMap: Partial<Record<VehicleKind, VehicleType>> = {
  [VehicleKind.VAN]: 'car',
  [VehicleKind.BUS]: 'car',
  [VehicleKind.WAGON]: 'car',
  [VehicleKind.SEDAN]: 'car',
  [VehicleKind.HATCHBACK]: 'car',
  [VehicleKind.CONVERTIBLE]: 'car',
  [VehicleKind.COUPE_2_DOOR]: 'car',
  [VehicleKind.ALL_TERRAIN_VEHICLE]: 'car',
  [VehicleKind.MPV]: 'car',
  [VehicleKind.PICKUP]: 'car',
  [VehicleKind.INCOMPLETE_VEHICLE]: 'car',
  [VehicleKind.PASSENGER_CAR]: 'car',
  [VehicleKind.LUXURY]: 'classic_car',
  [VehicleKind.TRUCK]: 'truck',
  [VehicleKind.MOTORCYCLE]: 'moto',
  [VehicleKind.TRAILER]: 'rv',
  [VehicleKind.BOAT]: 'boat'
};

const validCarKinds = new Set([
  VehicleKind.VAN,
  VehicleKind.BUS,
  VehicleKind.WAGON,
  VehicleKind.SEDAN,
  VehicleKind.HATCHBACK,
  VehicleKind.CONVERTIBLE,
  VehicleKind.COUPE_2_DOOR,
  VehicleKind.ALL_TERRAIN_VEHICLE,
  VehicleKind.MPV,
  VehicleKind.PICKUP,
  VehicleKind.INCOMPLETE_VEHICLE,
  VehicleKind.PASSENGER_CAR
]);

export const mappedVehicleKindFromType = (type: VehicleType, kind: VehicleKind | '') => {
  if (type === 'car' && validCarKinds.has(kind)) {
    return kind;
  }

  return vehicleTypeToKindMap[type] || VehicleKind.UNKNOWN;
};
